import { integrationApi } from "@repo/api";
import { InputField, Typography } from "@saviynt/design-system";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { isIspmHost } from "../../../config/integrationTypeConfig";
import { theme } from "../../../constants/theme";
import { useIntegrationById } from "../../../hooks/useIntegrationById";
import { useIntegrationTypeFeatures } from "../../../hooks/useIntegrationTypeFeatures";
import { useTestConnection } from "../../../hooks/useTestConnection";
import {
  type Frequency,
  DEFAULT_FREQUENCY,
  parseCronToSchedule,
} from "../../../utils/schedule";
import { AdditionalConfigurationSection } from "../../aws/AWSIntegrationForm/AdditionalConfigurationSection";
import { getDefaultTime } from "../../aws/AWSIntegrationForm/utils";
import { FormSection } from "../../FormSection";
import { IntegrationTypeSection } from "../../shared/IntegrationTypeSection";
import { TestConnectionBlock } from "../../TestConnectionBlock";
import {
  CorrelationFieldsProvider,
  fieldsToOptions,
} from "../../UserAccountCorrelation/CorrelationFieldsContext";
import { CorrelationSection } from "../../UserAccountCorrelation/CorrelationSection";

export interface AzureFoundryDiscoveryFormData {
  name: string;
  description: string;
  clientId: string;
  clientSecret: string;
  // `domain` is shown to the user as "Tenant ID" — they refer to the same value.
  // The backend payload key remains `domain`.
  domain: string;
  subscriptionId: string;
  isValid: boolean;
  cftDownloaded: boolean;
  importTime?: Date | null;
}

interface AzureFoundryDiscoveryFormProps {
  integrationType: string;
  integrationId?: number;
  onFormDataChange: (data: Record<string, unknown>) => void;
  onClose: () => void;
}

export function AzureFoundryDiscoveryForm({
  integrationType,
  integrationId,
  onFormDataChange,
}: AzureFoundryDiscoveryFormProps) {
  const intl = useIntl();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");
  const [domain, setDomain] = useState("");
  const [subscriptionId, setSubscriptionId] = useState("");
  const [clientSecretVisible, setClientSecretVisible] = useState(false);
  const [importTime, setImportTime] = useState<Date | null>(() =>
    getDefaultTime(),
  );
  const [frequency, setFrequency] = useState<Frequency>(DEFAULT_FREQUENCY);
  const hasPrefilledIntegrationRef = useRef(false);
  const hasPrefilledCredentialsRef = useRef(false);
  const onFormDataChangeRef = useRef(onFormDataChange);
  onFormDataChangeRef.current = onFormDataChange;

  const { data: integrationData } = useIntegrationById(integrationId);
  const { data: credentials } = useQuery({
    queryKey: ["integrationCredentials", integrationId],
    queryFn: async () => {
      if (!integrationId) throw new Error("Integration ID required");
      return integrationApi.getIntegrationCredentials(integrationId);
    },
    enabled: !!integrationId,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });

  const isIspm = isIspmHost();
  const { data: userFields } = useQuery({
    queryKey: ["correlationRuleFields", "user"],
    queryFn: () => integrationApi.getCorrelationRuleFields("user"),
    enabled: isIspm,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const { data: accountFields } = useQuery({
    queryKey: ["correlationRuleFields", "account"],
    queryFn: () => integrationApi.getCorrelationRuleFields("account"),
    enabled: isIspm,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const correlationUserOptions = useMemo(
    () => fieldsToOptions(userFields?.fields ?? []),
    [userFields],
  );
  const correlationAccountOptions = useMemo(
    () => fieldsToOptions(accountFields?.fields ?? []),
    [accountFields],
  );

  const { data: featuresResponse } =
    useIntegrationTypeFeatures(integrationType);
  const featureNames = useMemo(
    () =>
      featuresResponse?.features
        ? featuresResponse.features.map((f) => f.featureName)
        : [],
    [featuresResponse?.features],
  );

  useEffect(() => {
    if (!integrationId || !integrationData) return;

    if (!hasPrefilledIntegrationRef.current) {
      setName(integrationData.displayName ?? "");
      setDescription((integrationData.metadata?.description as string) ?? "");
      setDomain(
        (integrationData.metadata?.domain as string) ??
          ((credentials?.data as Record<string, unknown> | undefined)
            ?.domain as string) ??
          "",
      );
      setSubscriptionId((integrationData.metadata?.accountId as string) ?? "");

      if (
        integrationData.features?.length &&
        integrationData.features[0].schedules?.length
      ) {
        const cronExpression =
          integrationData.features[0].schedules[0].cronExpression;
        if (cronExpression) {
          const parsed = parseCronToSchedule(cronExpression);
          setFrequency(parsed.frequency);
          if (parsed.time) {
            setImportTime(parsed.time);
          }
        }
      }
      hasPrefilledIntegrationRef.current = true;
    }

    const creds = credentials as
      | {
          data?: {
            clientId?: string;
            clientSecret?: string;
            domain?: string;
          };
        }
      | undefined;
    const credsData = creds?.data;
    if (creds && !hasPrefilledCredentialsRef.current) {
      if (credsData?.clientId != null && credsData.clientId !== "") {
        setClientId(credsData.clientId);
      }
      if (credsData?.clientSecret != null && credsData.clientSecret !== "") {
        setClientSecret(credsData.clientSecret);
      }
      if (credsData?.domain != null && credsData.domain !== "") {
        setDomain(credsData.domain);
      }
      hasPrefilledCredentialsRef.current = true;
    }
  }, [integrationId, integrationData, credentials]);

  const testConnection = useTestConnection(() => {
    const trimmedClientId = clientId.trim();
    const trimmedClientSecret = clientSecret.trim();
    // `domain` here is the value entered in the "Tenant ID" field — same value, payload key stays `domain`.
    const trimmedDomain = domain.trim();
    const trimmedSubscriptionId = subscriptionId.trim();
    if (
      !trimmedClientId ||
      !trimmedClientSecret ||
      !trimmedDomain ||
      !trimmedSubscriptionId
    ) {
      return null;
    }
    return {
      integrationType: "AzurePlatform",
      credentials: {
        type: "OAUTH2",
        data: {
          clientId: trimmedClientId,
          clientSecret: trimmedClientSecret,
          domain: trimmedDomain,
        },
        metadata: {},
      },
      integrationParameters: {
        accountId: trimmedSubscriptionId,
      },
    };
  });

  const isNameValid = name.trim().length > 0;
  const isClientIdValid = clientId.trim().length > 0;
  const isClientSecretValid =
    clientSecret.trim().length > 0 || (!!integrationId && !!clientId);
  const isDomainValid = domain.trim().length > 0;
  const isValid =
    isNameValid &&
    isClientIdValid &&
    isClientSecretValid &&
    isDomainValid &&
    (frequency !== "24hours" || importTime !== null);
  const cftDownloaded = isClientIdValid && isClientSecretValid && isDomainValid;

  useEffect(() => {
    onFormDataChangeRef.current({
      name: name.trim(),
      description: description.trim(),
      clientId: clientId.trim(),
      clientSecret: clientSecret,
      domain: domain.trim(),
      subscriptionId: subscriptionId.trim(),
      frequency,
      importTime,
      isValid,
      cftDownloaded,
      featureNames,
      integrationType,
    });
  }, [
    name,
    description,
    clientId,
    clientSecret,
    domain,
    subscriptionId,
    frequency,
    importTime,
    isValid,
    cftDownloaded,
    featureNames,
    integrationType,
  ]);

  return (
    <div
      className="Integrations-addIntegrationForm flex flex-col h-full w-full p-4"
      data-testid="azure-foundry-discovery-form"
    >
      <div className="flex-1 overflow-auto p-4">
        <div className={`flex flex-col ${theme.spacing.sectionGap}`}>
          <IntegrationTypeSection integrationType="AzurePlatform" />
          <FormSection
            icon="Connect"
            title={intl.formatMessage({
              id: "LjAaeM",
              defaultMessage:
                "What are the connection details of your integration?",
              description: "Azure Foundry form section title",
            })}
            subtitle={intl.formatMessage({
              defaultMessage:
                "Some fields have been pre-filled for you, you can update them whenever you like.",
              id: "yJ4X4b",
              description: "Azure Foundry form section subtitle",
            })}
          >
            <div className={`flex flex-col ${theme.spacing.sectionGap}`}>
              <InputField
                name="name"
                label={
                  <Typography kind="label" htmlFor="azure-foundry-name">
                    <FormattedMessage
                      id="JeJjeJ"
                      defaultMessage="Integration Name"
                      description="Integration name field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={name}
                setValue={setName}
                isRequired
                isDisabled={!!integrationId}
              />

              <InputField
                name="description"
                placeholder=""
                label={
                  <Typography
                    kind="label"
                    className="block mb-1"
                    htmlFor="azure-foundry-description"
                  >
                    <FormattedMessage
                      id="TjUCGI"
                      defaultMessage="Integration Description"
                      description="Integration description field label"
                    />
                  </Typography>
                }
                value={description}
                setValue={setDescription}
                kind="multiline"
                rows={4}
              />

              {/*
                "Tenant ID" field — bound to `domain` state because the backend payload
                key is `domain`. Tenant ID and Domain refer to the same value here.
              */}
              <InputField
                name="domain"
                label={
                  <Typography kind="label" htmlFor="azure-foundry-domain">
                    <FormattedMessage
                      id="LG3Bq9"
                      defaultMessage="Tenant ID"
                      description="Tenant ID field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={domain}
                setValue={setDomain}
                isRequired
              />

              <InputField
                name="subscriptionId"
                label={
                  <Typography
                    kind="label"
                    htmlFor="azure-foundry-subscription-id"
                  >
                    <FormattedMessage
                      id="UxVz2i"
                      defaultMessage="Subscription ID"
                      description="Subscription ID field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={subscriptionId}
                setValue={setSubscriptionId}
                isRequired
              />

              <InputField
                name="clientId"
                label={
                  <Typography kind="label" htmlFor="azure-foundry-client-id">
                    <FormattedMessage
                      id="YB3Qau"
                      defaultMessage="Client ID"
                      description="Client ID field label"
                    />
                  </Typography>
                }
                placeholder=""
                value={clientId}
                setValue={setClientId}
                isRequired
              />

              <InputField
                name="clientSecret"
                label={
                  <Typography
                    kind="label"
                    htmlFor="azure-foundry-client-secret"
                  >
                    <FormattedMessage
                      id="Og17uQ"
                      defaultMessage="Client Secret"
                      description="Client Secret field label"
                    />
                  </Typography>
                }
                placeholder=""
                type={clientSecretVisible ? "text" : "password"}
                value={clientSecret}
                setValue={setClientSecret}
                isRequired={!integrationId}
                suffixButtonType="password"
                suffixIsToggle={clientSecretVisible}
                suffixOnClick={() =>
                  setClientSecretVisible(!clientSecretVisible)
                }
              />

              <TestConnectionBlock
                isTestingConnection={testConnection.isTestingConnection}
                connectionStatus={testConnection.connectionStatus}
                onTestConnection={testConnection.handleTestConnection}
                isDisabled={
                  !isClientIdValid || !clientSecret.trim() || !isDomainValid
                }
              />
            </div>
          </FormSection>

          {isIspm && !!integrationId && (
            <FormSection
              icon="User"
              title={intl.formatMessage({
                id: "s/4S0e",
                defaultMessage: "User Account Correlation",
                description: "User Account Correlation section title",
              })}
              subtitle={intl.formatMessage({
                id: "Q7LnPy",
                defaultMessage:
                  "Define rules for correlating user identities with their cloud accounts. These rules help establish the relationship between enterprise users and their cloud identities. You can update them whenever you like.",
                description: "User Account Correlation section subtitle",
              })}
            >
              <CorrelationFieldsProvider
                userFieldOptions={correlationUserOptions}
                accountFieldOptions={correlationAccountOptions}
              >
                <CorrelationSection integrationId={integrationId} />
              </CorrelationFieldsProvider>
            </FormSection>
          )}

          <AdditionalConfigurationSection
            initialTime={importTime}
            initialFrequency={frequency}
            onScheduleChange={(freq, time) => {
              setFrequency(freq as Frequency);
              setImportTime(time);
            }}
            isRequired={true}
            isCritical={frequency === "24hours" && importTime === null}
          />
        </div>
      </div>
    </div>
  );
}
